import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Send, CheckCircle2, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, SERVICE_TYPE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { Page, Avatar } from "@/components/layout/AppShell";
import { SnapshotView } from "@/components/TarotCard";
import { Media, Uploader } from "@/components/Uploader";

const STATUS: Record<string, string> = { paid: "Awaiting reader", in_progress: "In progress", delivered: "Delivered", completed: "Completed", cancelled: "Cancelled" };

export default function ReadingRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState<any>(null);
  const [deck, setDeck] = useState<any[]>([]);
  const [text, setText] = useState("");
  const [snapId, setSnapId] = useState("");
  const [snaps, setSnaps] = useState<any[]>([]);
  const [deliver, setDeliver] = useState(false);
  const [dText, setDText] = useState("");
  const [dMedia, setDMedia] = useState("");
  const [mMedia, setMMedia] = useState("");
  const [review, setReview] = useState({ rating: 5, body: "" });
  const end = useRef<HTMLDivElement>(null);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/table/deck").then((r) => setDeck(r.data.cards)); const t = setInterval(load, 6000); return () => clearInterval(t); }, [id]);
  useEffect(() => { if (user) api.get("/table/snapshots").then((r) => setSnaps(r.data)); }, [user, deliver]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [b?.messages?.length]);

  if (!b) return <Page><div className="card-tactile h-64 animate-pulse" /></Page>;
  const isReader = user?.id === b.reader_id;
  const other = isReader ? b.customer : b.reader;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() && !snapId && !mMedia) return;
    try { await api.post(`/bookings/${id}/messages`, { body: text, snapshot_id: snapId || null, media_url: mMedia || null }); setText(""); setSnapId(""); setMMedia(""); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  const doDeliver = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post(`/bookings/${id}/deliver`, { body: dText, media_url: dMedia || null, snapshot_id: snapId || null }); toast.success("Reading delivered"); setDeliver(false); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  const sendReview = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post(`/bookings/${id}/review`, review); toast.success("Thank you for your review"); load(); } catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <Page className="max-w-4xl" >
      <div className="card-tactile p-4 flex flex-wrap items-center gap-4 mb-4" data-testid="reading-meta">
        <Avatar u={other} size={44} />
        <div className="flex-1 min-w-0">
          <p className="serif text-xl leading-none truncate">{b.service_title}</p>
          <p className="mono text-[10px] text-[#5E5C56] mt-1">{isReader ? "Client" : "Reader"}: {other?.name} · {SERVICE_TYPE_LABEL[b.service_type]} · {money(b.amount)}{b.scheduled_at ? ` · ${new Date(b.scheduled_at).toLocaleString()}` : ""}</p>
        </div>
        <span className={`mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${b.status === "delivered" || b.status === "completed" ? "bg-[#2D5A3F] text-[#F6F4EE]" : "bg-[#1A1917] text-[#F6F4EE]"}`} data-testid="reading-status">{STATUS[b.status] || b.status}</span>
        {isReader && ["paid", "in_progress"].includes(b.status) && <button onClick={() => setDeliver(true)} className="btn-brass !py-2" data-testid="open-deliver-btn"><CheckCircle2 size={16} /> Deliver reading</button>}
        <Link to={`/table?booking=${b.id}`} className="btn-ghost !py-2" data-testid="open-table-btn"><LayoutGrid size={16} /> The Table</Link>
      </div>

      <div className="card-tactile p-5 mb-4">
        <p className="eyebrow mb-1">Question</p>
        <p className="text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="reading-question">{b.question}</p>
      </div>

      {b.delivery && (
        <div className="rounded-md border border-[#C5A059] bg-[#F6F4EE] p-6 mb-4 rise" data-testid="reading-delivery">
          <p className="eyebrow !text-[#581C25] mb-2">Your reading · delivered {new Date(b.delivery.delivered_at).toLocaleDateString()}</p>
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap serif !text-lg">{b.delivery.body}</p>
          {b.delivery.media_url && <div className="mt-4"><Media url={b.delivery.media_url} authed /></div>}
          {b.delivery.snapshot && <div className="mt-4"><SnapshotView snap={b.delivery.snapshot} deck={deck} /></div>}
        </div>
      )}

      <div className="card-tactile p-4 sm:p-5 space-y-3 max-h-[50vh] overflow-y-auto" data-testid="reading-thread">
        {b.messages.length === 0 && <p className="text-sm text-[#5E5C56] text-center py-6">The room is open. {isReader ? "Say hello to your client." : "Your reader will be with you soon."}</p>}
        {b.messages.map((m: any) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-md px-3.5 py-2.5 text-sm ${mine ? "bg-[#1A1917] text-[#F6F4EE]" : "bg-[#F6F4EE] border border-[#D8D4C9]"}`}>
                {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                {m.media_url && <div className="mt-2 min-w-[220px]"><Media url={m.media_url} authed /></div>}
                {m.snapshot && <div className="mt-2"><SnapshotView snap={m.snapshot} deck={deck} compact /></div>}
                <p className={`mono text-[9px] mt-1 flex items-center gap-2 ${mine ? "text-[#A8A59C]" : "text-[#5E5C56]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{!mine && <ReportButton type="message" id={m.id} className="!text-[10px]" />}</p>
              </div>
            </div>);
        })}
        <div ref={end} />
      </div>

      {!["cancelled"].includes(b.status) && (
        <form onSubmit={send} className="mt-3 flex gap-2 items-start">
          <div className="flex-1">
            <textarea className="input-ed min-h-[44px]" rows={1} placeholder="Write a message…" value={text} onChange={(e) => setText(e.target.value)} data-testid="room-message-input" />
            {mMedia && <p className="mono text-[10px] text-[#5E5C56] mt-1">Attached: {mMedia.split("/").pop()} <button type="button" className="underline" onClick={() => setMMedia("")}>remove</button></p>}
            <div className="flex gap-2 mt-2 items-start">
              <div className="w-44"><Uploader accept="any" visibility="private" onDone={(f) => setMMedia(f[0].url)} label="Attach voice/video/file" testId="room-upload" /></div>
              {isReader && snaps.length > 0 && (
              <select className="input-ed !py-1.5 text-xs flex-1" value={snapId} onChange={(e) => setSnapId(e.target.value)} data-testid="room-snapshot-select">
                <option value="">Attach a Table snapshot (optional)</option>
                {snaps.map((s) => <option key={s.id} value={s.id}>{s.title} · {s.spread_name}</option>)}
              </select>)}
            </div>
          </div>
          <button className="btn-wine !px-4" data-testid="room-send-btn"><Send size={16} /></button>
        </form>
      )}

      {!isReader && b.status === "delivered" && !b.review && (
        <form onSubmit={sendReview} className="card-tactile p-5 mt-6 space-y-3" data-testid="review-form">
          <p className="serif text-xl">How was your reading?</p>
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setReview({ ...review, rating: n })} className={`text-2xl ${n <= review.rating ? "text-[#C5A059]" : "text-[#D8D4C9]"}`} data-testid={`star-${n}`}>★</button>)}</div>
          <textarea className="input-ed" placeholder="A few words for other members" value={review.body} onChange={(e) => setReview({ ...review, body: e.target.value })} data-testid="review-body" />
          <button className="btn-wine" data-testid="review-submit">Submit review & complete</button>
        </form>
      )}
      {b.review && <p className="mono text-xs text-[#5E5C56] mt-4">Reviewed {"★".repeat(b.review.rating)} — “{b.review.body}”</p>}

      {deliver && (
        <div className="fixed inset-0 z-50 bg-[#121110]/70 flex items-end sm:items-center justify-center p-4" onClick={() => setDeliver(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={doDeliver} className="bg-[#F6F4EE] rounded-md w-full max-w-lg p-6 space-y-3 rise" data-testid="deliver-modal">
            <p className="serif text-2xl">Deliver the reading</p>
            <textarea className="input-ed min-h-[180px]" placeholder="Write the full reading here…" value={dText} onChange={(e) => setDText(e.target.value)} required data-testid="deliver-body" />
            <Uploader accept="any" visibility="private" onDone={(f) => setDMedia(f[0].url)} label="Upload voice note / video / PDF" testId="deliver-upload" />
            {dMedia && <p className="mono text-[10px] text-[#5E5C56]">Attached: {dMedia.split("/").pop()}</p>}
            <select className="input-ed" value={snapId} onChange={(e) => setSnapId(e.target.value)} data-testid="deliver-snapshot">
              <option value="">Attach a Table snapshot (optional)</option>
              {snaps.map((s) => <option key={s.id} value={s.id}>{s.title} · {s.spread_name}</option>)}
            </select>
            <div className="flex gap-2 justify-end"><button type="button" onClick={() => setDeliver(false)} className="btn-ghost">Cancel</button><button className="btn-wine" data-testid="deliver-submit">Deliver</button></div>
          </form>
        </div>
      )}
    </Page>
  );
}
